import type {
    MediaDocType,
    MediaLinkDocType,
    MediaOwnerType
} from '@ast-data/shared';
import type { RxCollection } from 'rxdb';
import type { AppDatabase } from '../../db/database';
import { forgetMedia } from './blobStore';
import { planOwnerRemoval, type OwnerRemovalPlan } from './mediaLinks';

/** What deleting some owners does to their pictures, in counts for a dialog. */
export interface MediaRemovalImpact {
    photos: number;
    sketches: number;
    /** Pictures that stay because they are linked into something that does. */
    moved: number;
}

/** The owners that go, as `ownerType` + `ownerId`. */
export interface GoingOwner {
    ownerType: MediaOwnerType;
    ownerId: string;
}

/**
 * Read what the pictures of these owners need to know, and plan their fate
 * with `planOwnerRemoval`: their own pictures, every link into them, and every
 * link OF their pictures, since those decide where a picture can move to.
 */
export async function planMediaRemoval(
    database: AppDatabase,
    owners: readonly GoingOwner[]
): Promise<{ plan: OwnerRemovalPlan; media: MediaDocType[] }> {
    const keys = new Set(
        owners.map((owner) => `${owner.ownerType}:${owner.ownerId}`)
    );
    const ownerIds = owners.map((owner) => owner.ownerId);
    const json = <T>(docs: { toMutableJSON(): unknown }[]) =>
        docs.map((doc) => doc.toMutableJSON() as T);

    const media = json<MediaDocType>(
        await database.media
            .find({ selector: { ownerId: { $in: ownerIds } } })
            .exec()
    ).filter((item) => keys.has(`${item.ownerType}:${item.ownerId}`));
    const linksInto = json<MediaLinkDocType>(
        await database.media_links
            .find({ selector: { ownerId: { $in: ownerIds } } })
            .exec()
    );
    const linksOf = json<MediaLinkDocType>(
        await database.media_links
            .find({
                selector: { mediaId: { $in: media.map((item) => item.id) } }
            })
            .exec()
    );
    const links = [
        ...new Map(
            [...linksInto, ...linksOf].map((link) => [link.id, link])
        ).values()
    ];
    // A picture only moves to a description that is still there.
    const candidateIds = [...new Set(linksOf.map((link) => link.ownerId))];
    const live = await Promise.all(
        [
            database.findings,
            database.finding_planums,
            database.finding_profiles,
            database.overall_descriptions,
            database.area_descriptions,
            database.samples,
            database.diaries
        ].map(async (collection) =>
            (
                await (collection as unknown as RxCollection<{ id: string }>)
                    .find({ selector: { id: { $in: candidateIds } } })
                    .exec()
            ).map((doc) => doc.id)
        )
    );
    const liveOwnerIds = new Set(live.flat());
    return {
        plan: planOwnerRemoval(keys, media, links, liveOwnerIds),
        media
    };
}

/** The confirm dialog's counts for a plan. */
export function mediaRemovalImpact(
    plan: OwnerRemovalPlan,
    media: readonly MediaDocType[]
): MediaRemovalImpact {
    const deleted = new Set(plan.deleteMediaIds);
    const going = media.filter((item) => deleted.has(item.id));
    return {
        photos: going.filter((item) => item.kind !== 'sketch').length,
        sketches: going.filter((item) => item.kind === 'sketch').length,
        moved: plan.moves.length
    };
}

/**
 * Carry out a plan. Moves first, so a picture that lives on is never without
 * an owner; then the links, then the pictures that go, then the going owners'
 * picture groups. The binaries stay on the server, as with any soft delete;
 * only this device's copies are dropped.
 */
export async function applyMediaRemoval(
    database: AppDatabase,
    plan: OwnerRemovalPlan
): Promise<void> {
    const now = new Date().toISOString();
    for (const move of plan.moves) {
        await (
            await database.media.findOne(move.mediaId).exec()
        )?.incrementalPatch({
            ownerType: move.ownerType,
            ownerId: move.ownerId,
            projectId: move.projectId,
            updatedAt: now
        });
    }
    for (const id of plan.deleteLinkIds) {
        await (await database.media_links.findOne(id).exec())?.remove();
    }
    for (const id of plan.deleteMediaIds) {
        await (await database.media.findOne(id).exec())?.remove();
        await forgetMedia(id);
    }
    if (plan.goingOwnerIds.length > 0) {
        await database.media_groups
            .find({ selector: { ownerId: { $in: plan.goingOwnerIds } } })
            .remove();
    }
}
